// ProfitabilityCalculator.js

import React, { useState } from 'react';
import {
  Grid,
  Typography,
  Button,
  Slider,
  Paper,
  Checkbox,
  FormControlLabel,
  IconButton,
  Tooltip,
  Box,
} from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, Legend } from 'recharts';
import { NumericFormat } from 'react-number-format';
import { v4 as uuidv4 } from 'uuid';
import { motion } from 'framer-motion';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import styled from 'styled-components';
import { styled as muiStyled } from '@mui/material/styles';
import { Card, CardContent } from '@mui/material';
import { alpha } from '@mui/material/styles';
import * as XLSX from 'xlsx';
import { Table as AntTable, ConfigProvider } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { blue, lightBlue, blueGrey, red, green } from '@mui/material/colors';
import { ColorPicker } from 'antd';

// Theme and styled components
const theme = createTheme({
  palette: {
    primary: {
      main: blue[700],
      light: blue[300],
      dark: blue[900],
    },
    secondary: {
      main: blueGrey[600],
      light: blueGrey[400],
      dark: blueGrey[800],
    },
    background: {
      default: lightBlue[50],
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: "'Poppins', sans-serif",
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 30,
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 15,
          },
        },
      },
    },
  },
});

const GlassCard = muiStyled(Card)(({ theme }) => ({
  background: alpha(theme.palette.background.paper, 0.8),
  backdropFilter: 'blur(10px)',
  borderRadius: 30,
  boxShadow: `0 8px 32px 0 ${alpha(theme.palette.primary.main, 0.2)}`,
  border: `1px solid ${alpha(theme.palette.primary.light, 0.18)}`,
  padding: theme.spacing(4),
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: `0 12px 40px 0 ${alpha(theme.palette.primary.main, 0.25)}`,
  },
}));

const ColorButton = muiStyled(Button)(({ theme, color }) => ({
  color: theme.palette.getContrastText(color),
  backgroundColor: color,
  '&:hover': {
    backgroundColor: alpha(color, 0.9),
  },
  borderRadius: 30,
  padding: '10px 20px',
  fontWeight: 600,
}));

const StyledTextField = styled.input`
  width: 100%;
  padding: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  font-size: 16px;
  font-family: 'Poppins', sans-serif;
  outline: none;
  transition: border-color 0.3s ease;
  
  &:focus {
    border-color: ${blue[500]};
  }
`;

const StyledSlider = styled(Slider)`
  color: ${blue[500]};
`;

const SectionSubtitle = styled(Typography)`
  font-size: 1.2rem;
  color: #4a4a4a;
  margin-bottom: 1rem;
  font-weight: 500;
`;

const StyledTableContainer = styled('div')`
  .ant-table {
    background: white;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  }

  .ant-table-thead > tr > th {
    background: white !important;
    color: rgb(75, 85, 99) !important;
    font-weight: 500;
    padding: 16px;
    border-bottom: 1px solid #f0f0f0;
    
    &:hover {
      background: #fafafa !important;
    }
    
    &.ant-table-column-has-sorters:hover {
      background: #fafafa !important;
    }
  }

  .ant-table-tbody > tr > td {
    padding: 16px;
    border-bottom: 1px solid #f0f0f0;
  }

  .ant-table-tbody > tr:nth-of-type(even) {
    background-color: #fafafa;
  }

  .ant-table-tbody > tr:hover > td {
    background-color: #f5f5f5 !important;
  }

  @media (max-width: 767px) {
    .ant-table {
      font-size: 0.875rem;
    }
    
    .ant-table-thead > tr > th {
      padding: 8px 4px;
      white-space: nowrap;
    }
    
    .ant-table-tbody > tr > td {
      padding: 8px 4px;
    }

    .ant-space {
      gap: 4px !important;
    }
  }
`;

const ProfitabilityCalculator = () => {
  // State variables
  const [scenarios, setScenarios] = useState([]);
  const [currentScenario, setCurrentScenario] = useState({
    id: uuidv4(),
    name: "Plan 1",
    hoursPerQuarter: 10,
    quarterlyFee: 2500,
    advisorHourlyCost: 150,
    staffHourlyCost: 75,
    advisorHoursPerQuarter: 6,
    staffHoursPerQuarter: 4,
    profitabilityThreshold: 20,
    includeInGraph: true,
    color: '#4a90e2',
  });

  // Profitability calculation functions
  const calculateProfitability = (scenario) => {
    const annualFee = scenario.quarterlyFee * 4;
    const totalHoursPerQuarter = scenario.advisorHoursPerQuarter + scenario.staffHoursPerQuarter;
    const totalHoursPerYear = totalHoursPerQuarter * 4;
    const totalCostPerQuarter = (scenario.advisorHoursPerQuarter * scenario.advisorHourlyCost) + 
                               (scenario.staffHoursPerQuarter * scenario.staffHourlyCost);
    const totalCostPerYear = totalCostPerQuarter * 4;
    const profitPerYear = annualFee - totalCostPerYear;
    const profitMargin = annualFee > 0 ? (profitPerYear / annualFee) * 100 : 0;
    const belowThreshold = profitMargin < scenario.profitabilityThreshold;
    
    return {
      annualFee,
      totalHoursPerYear,
      totalCostPerYear,
      profitPerYear,
      profitMargin,
      belowThreshold,
    };
  };

  const generateProfitabilityData = () => {
    const scenariosToAnalyze = scenarios.length > 0 ? scenarios : [currentScenario];
    
    return scenariosToAnalyze.map(scenario => {
      const analysis = calculateProfitability(scenario);
      return {
        name: scenario.name,
        fee: analysis.annualFee,
        cost: analysis.totalCostPerYear,
        profit: analysis.profitPerYear,
        margin: analysis.profitMargin,
        hours: analysis.totalHoursPerYear,
        belowThreshold: analysis.belowThreshold,
        color: scenario.color,
      };
    });
  };

  // Scenario management functions
  const handleAddOrUpdateScenario = () => {
    const existingScenarioIndex = scenarios.findIndex(s => s.id === currentScenario.id);
    
    if (existingScenarioIndex !== -1) {
      const updatedScenarios = [...scenarios];
      updatedScenarios[existingScenarioIndex] = { ...currentScenario };
      setScenarios(updatedScenarios);
    } else {
      const newScenarioName = `Plan ${scenarios.length + 1}`;
      setScenarios([...scenarios, { ...currentScenario, name: newScenarioName }]);
    }

    setCurrentScenario({
      id: uuidv4(),
      name: `Plan ${scenarios.length + 2}`,
      hoursPerQuarter: 10,
      quarterlyFee: 2500,
      advisorHourlyCost: 150,
      staffHourlyCost: 75,
      advisorHoursPerQuarter: 6,
      staffHoursPerQuarter: 4,
      profitabilityThreshold: 20,
      includeInGraph: true,
      color: `hsl(${Math.random() * 360}, 70%, 50%)`,
    });
  };

  const handleEditScenario = (id) => {
    const scenarioToEdit = scenarios.find(scenario => scenario.id === id);
    setCurrentScenario(scenarioToEdit);
  };

  const handleRemoveScenario = (id) => {
    const updatedScenarios = scenarios.filter((scenario) => scenario.id !== id);
    const renamedScenarios = updatedScenarios.map((scenario, index) => ({
      ...scenario,
      name: `Plan ${index + 1}`
    }));
    setScenarios(renamedScenarios);
    
    setCurrentScenario(prev => ({
      ...prev,
      name: `Plan ${renamedScenarios.length + 1}`
    }));
  };

  // Format numbers with commas
  const formatNumber = (value) => {
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // Table state and handling
  const [sortedInfo, setSortedInfo] = useState({});
  const handleTableChange = (pagination, filters, sorter) => {
    setSortedInfo(sorter);
  };

  // Profitability table columns
  const columns = [
    {
      title: 'Plan Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      sortOrder: sortedInfo.columnKey === 'name' && sortedInfo.order,
    },
    {
      title: 'Annual Fee',
      dataIndex: 'fee',
      key: 'fee',
      sorter: (a, b) => a.fee - b.fee,
      sortOrder: sortedInfo.columnKey === 'fee' && sortedInfo.order,
      render: (value) => `$${formatNumber(value.toFixed(2))}`,
    },
    {
      title: 'Annual Cost',
      dataIndex: 'cost',
      key: 'cost',
      sorter: (a, b) => a.cost - b.cost,
      sortOrder: sortedInfo.columnKey === 'cost' && sortedInfo.order,
      render: (value) => `$${formatNumber(value.toFixed(2))}`,
    },
    {
      title: 'Annual Profit',
      dataIndex: 'profit',
      key: 'profit',
      sorter: (a, b) => a.profit - b.profit,
      sortOrder: sortedInfo.columnKey === 'profit' && sortedInfo.order,
      render: (value) => (
        <span style={{ color: value >= 0 ? green[600] : red[600], fontWeight: 'bold' }}>
          ${formatNumber(value.toFixed(2))}
        </span>
      ),
    },
    {
      title: 'Profit Margin %',
      dataIndex: 'margin',
      key: 'margin',
      sorter: (a, b) => a.margin - b.margin,
      sortOrder: sortedInfo.columnKey === 'margin' && sortedInfo.order,
      render: (value) => (
        <span style={{ color: value >= 20 ? green[600] : red[600], fontWeight: 'bold' }}>
          {value.toFixed(1)}%
        </span>
      ),
    },
    {
      title: 'Annual Hours',
      dataIndex: 'hours',
      key: 'hours',
      sorter: (a, b) => a.hours - b.hours,
      sortOrder: sortedInfo.columnKey === 'hours' && sortedInfo.order,
      render: (value) => `${formatNumber(value)} hrs`,
    },
    {
      title: 'Below Threshold?',
      dataIndex: 'belowThreshold',
      key: 'belowThreshold',
      sorter: (a, b) => a.belowThreshold - b.belowThreshold,
      sortOrder: sortedInfo.columnKey === 'belowThreshold' && sortedInfo.order,
      render: (value) => (
        <span style={{ 
          color: value ? red[600] : green[600], 
          fontWeight: 'bold',
          padding: '4px 8px',
          borderRadius: '4px',
          backgroundColor: value ? red[50] : green[50]
        }}>
          {value ? 'Yes' : 'No'}
        </span>
      ),
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 10
      }
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        style={{ padding: '24px' }}
      >
        <Grid container spacing={4}>
          {/* Input Section */}
          <Grid item xs={12} md={6}>
            <GlassCard variants={itemVariants}>
              <CardContent>
                <SectionSubtitle variant="h6" gutterBottom sx={{ color: theme.palette.secondary.main }}>
                  Define Plan Profitability
                </SectionSubtitle>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>Plan Name</Typography>
                  <StyledTextField
                    value={currentScenario.name}
                    onChange={(e) =>
                      setCurrentScenario({ ...currentScenario, name: e.target.value })
                    }
                    placeholder="Plan Name"
                  />
                </Box>
                <NumericFormat
                  customInput={StyledTextField}
                  value={currentScenario.quarterlyFee}
                  onValueChange={(values) =>
                    setCurrentScenario({ ...currentScenario, quarterlyFee: values.value })
                  }
                  thousandSeparator={true}
                  prefix={'$'}
                  placeholder="Quarterly Fee"
                  style={{ marginBottom: '16px' }}
                />
                <NumericFormat
                  customInput={StyledTextField}
                  value={currentScenario.advisorHourlyCost}
                  onValueChange={(values) =>
                    setCurrentScenario({ ...currentScenario, advisorHourlyCost: values.value })
                  }
                  thousandSeparator={true}
                  prefix={'$'}
                  placeholder="Advisor Hourly Cost"
                  style={{ marginBottom: '16px' }}
                />
                <NumericFormat
                  customInput={StyledTextField}
                  value={currentScenario.staffHourlyCost}
                  onValueChange={(values) =>
                    setCurrentScenario({ ...currentScenario, staffHourlyCost: values.value })
                  }
                  thousandSeparator={true}
                  prefix={'$'}
                  placeholder="Staff Hourly Cost"
                  style={{ marginBottom: '16px' }}
                />
                <Typography gutterBottom>
                  Advisor Hours per Quarter: {currentScenario.advisorHoursPerQuarter}
                </Typography>
                <StyledSlider
                  value={currentScenario.advisorHoursPerQuarter}
                  onChange={(e, val) =>
                    setCurrentScenario({ ...currentScenario, advisorHoursPerQuarter: val })
                  }
                  aria-labelledby="advisor-hours-per-quarter"
                  valueLabelDisplay="auto"
                  step={0.5}
                  min={0}
                  max={100}
                />
                <Typography gutterBottom>
                  Staff Hours per Quarter: {currentScenario.staffHoursPerQuarter}
                </Typography>
                <StyledSlider
                  value={currentScenario.staffHoursPerQuarter}
                  onChange={(e, val) =>
                    setCurrentScenario({ ...currentScenario, staffHoursPerQuarter: val })
                  }
                  aria-labelledby="staff-hours-per-quarter"
                  valueLabelDisplay="auto"
                  step={0.5}
                  min={0}
                  max={100}
                />
                <Typography gutterBottom>
                  Profitability Threshold: {currentScenario.profitabilityThreshold}%
                </Typography>
                <StyledSlider
                  value={currentScenario.profitabilityThreshold}
                  onChange={(e, val) =>
                    setCurrentScenario({ ...currentScenario, profitabilityThreshold: val })
                  }
                  aria-labelledby="profitability-threshold"
                  valueLabelDisplay="auto"
                  step={1}
                  min={0}
                  max={50}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={currentScenario.includeInGraph}
                        onChange={(e) =>
                          setCurrentScenario({ ...currentScenario, includeInGraph: e.target.checked })
                        }
                        name="includeInGraph"
                        color="primary"
                      />
                    }
                    label="Include in Graph"
                  />
                  <ColorPicker
                    value={currentScenario.color}
                    onChange={(color) => setCurrentScenario({ ...currentScenario, color: color.toHexString() })}
                    trigger="hover"
                    disabledAlpha
                    presets={[
                      {
                        label: 'Recommended',
                        colors: [
                          '#4a90e2', '#50C878', '#FF6B6B', '#9B59B6',
                          '#34495E', '#2ECC71', '#E74C3C', '#8E44AD'
                        ],
                      },
                    ]}
                  />
                </Box>
                <Box sx={{ mt: 3 }}>
                  <ColorButton
                    variant="contained"
                    onClick={handleAddOrUpdateScenario}
                    startIcon={<AddIcon />}
                    color={currentScenario.color}
                    fullWidth
                  >
                    {scenarios.some(s => s.id === currentScenario.id) ? 'Update Plan' : 'Add Plan'}
                  </ColorButton>
                </Box>
              </CardContent>
            </GlassCard>
          </Grid>

          {/* Scenario List */}
          <Grid item xs={12} md={6}>
            <GlassCard variants={itemVariants}>
              <CardContent>
                <SectionSubtitle variant="h6" gutterBottom sx={{ color: theme.palette.secondary.main }}>
                  Plans
                </SectionSubtitle>
                {scenarios.length > 0 ? (
                  scenarios.map((scenario) => {
                    const analysis = calculateProfitability(scenario);
                    return (
                      <Box key={scenario.id} sx={{ mb: 2, position: 'relative' }}>
                        <Paper
                          elevation={3}
                          sx={{
                            p: 2,
                            borderRadius: '10px',
                            background: alpha(scenario.color, 0.1),
                            border: `1px solid ${alpha(scenario.color, 0.3)}`,
                          }}
                        >
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: scenario.color }}>
                            {scenario.name}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                            Annual Profit: <span style={{ color: analysis.profitPerYear >= 0 ? green[600] : red[600], fontWeight: 'bold' }}>
                              ${formatNumber(analysis.profitPerYear.toFixed(2))}
                            </span>
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Margin: <span style={{ color: analysis.profitMargin >= scenario.profitabilityThreshold ? green[600] : red[600], fontWeight: 'bold' }}>
                              {analysis.profitMargin.toFixed(1)}%
                            </span>
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={scenario.includeInGraph}
                                  onChange={(e) => {
                                    const newScenarios = scenarios.map((s) =>
                                      s.id === scenario.id ? { ...s, includeInGraph: e.target.checked } : s
                                    );
                                    setScenarios(newScenarios);
                                  }}
                                  name="includeInGraph"
                                  color="primary"
                                />
                              }
                              label="Include in Graph"
                            />
                            <Box>
                              <IconButton
                                color="primary"
                                onClick={() => handleEditScenario(scenario.id)}
                                size="small"
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                color="secondary"
                                onClick={() => handleRemoveScenario(scenario.id)}
                                size="small"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </Box>
                        </Paper>
                      </Box>
                    );
                  })
                ) : (
                  <Typography variant="body1">No saved plans. Create one to get started!</Typography>
                )}
              </CardContent>
            </GlassCard>
          </Grid>

          {/* Profitability Chart */}
          <Grid item xs={12}>
            <GlassCard variants={itemVariants}>
              <CardContent>
                <SectionSubtitle variant="h6" gutterBottom sx={{ color: theme.palette.secondary.main }}>
                  Profitability Comparison
                </SectionSubtitle>
                <ResponsiveContainer width="100%" height={500}>
                  <BarChart data={generateProfitabilityData()}>
                    <XAxis dataKey="name" />
                    <YAxis />
                    <RechartsTooltip 
                      formatter={(value, name) => {
                        if (name === 'fee' || name === 'cost' || name === 'profit') {
                          return [`$${formatNumber(value.toFixed(2))}`, name];
                        }
                        return [value, name];
                      }}
                    />
                    <Legend />
                    <Bar dataKey="fee" fill="#4a90e2" name="Annual Fee" />
                    <Bar dataKey="cost" fill="#ff6b6b" name="Annual Cost" />
                    <Bar dataKey="profit" fill="#50c878" name="Annual Profit" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </GlassCard>
          </Grid>

          {/* Data Table */}
          <Grid item xs={12}>
            <GlassCard variants={itemVariants} style={{ marginBottom: '2rem' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <SectionSubtitle variant="h6" sx={{ color: theme.palette.secondary.main }}>
                    Profitability Analysis
                  </SectionSubtitle>
                  <Tooltip title="Download Excel">
                    <Button
                      onClick={() => {
                        const worksheet = XLSX.utils.json_to_sheet(generateProfitabilityData());
                        const workbook = XLSX.utils.book_new();
                        XLSX.utils.book_append_sheet(workbook, worksheet, "Profitability Analysis");
                        XLSX.writeFile(workbook, "profitability_analysis.xlsx");
                      }}
                      startIcon={<DownloadOutlined />}
                      sx={{
                        color: theme.palette.secondary.main,
                        '&:hover': {
                          color: theme.palette.primary.main,
                          backgroundColor: 'transparent',
                        },
                        transition: 'all 0.3s ease',
                        textTransform: 'none',
                        fontSize: '14px',
                      }}
                    >
                      Download Excel
                    </Button>
                  </Tooltip>
                </Box>
                <ConfigProvider
                  theme={{
                    token: {
                      fontFamily: "'Poppins', sans-serif",
                      borderRadius: 12,
                      colorBgContainer: '#ffffff',
                      colorBorderSecondary: '#f0f0f0',
                      fontSize: 14,
                    },
                    components: {
                      Table: {
                        headerBg: '#ffffff',
                        headerColor: 'rgb(75, 85, 99)',
                        rowHoverBg: '#f5f5f5',
                        headerSortHoverBg: '#fafafa',
                        headerSortActiveBg: '#fafafa',
                        borderColor: '#f0f0f0',
                        rowExpandedBg: '#fafafa',
                      },
                    },
                  }}
                >
                  <StyledTableContainer>
                    <AntTable
                      columns={columns}
                      dataSource={generateProfitabilityData()}
                      rowKey="name"
                      onChange={handleTableChange}
                      pagination={{ 
                        pageSize: 10,
                        showSizeChanger: false,
                        showQuickJumper: true,
                        style: { marginTop: '16px' }
                      }}
                      scroll={{ x: 'max-content' }}
                    />
                  </StyledTableContainer>
                </ConfigProvider>
              </CardContent>
            </GlassCard>
          </Grid>
        </Grid>
      </motion.div>
    </ThemeProvider>
  );
};

export default ProfitabilityCalculator; 